import type { SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';
import type { ThemeRadii } from '@rover/kibble/styles/themeTypes';

import { breakpoints } from '../../themes/themeConstants';
import { MarginSystemProps } from '../styledSystemPropTypes';

/** Three srcset entries, `[mobile, tablet, desktop]`, one of which may be `null`. */
export type SrcsetArray =
  | [string, string, string]
  | [null, string, string]
  | [string, null, string]
  | [string, string, null];

/** Props shared by every platform. */
type ImageCommonProps = MarginSystemProps & {
  /**
   * The text screen readers read for the image. Pass `""` for a decorative image, so they skip it.
   */
  description: string;
  /**
   * Pixels as a number, or a CSS string such as `'300px'` or `'100%'`. Under Next.js, use a number,
   * or `'100%'` for both `height` and `width` to fill the parent.
   */
  height: string | number;
  /**
   * Pixels as a number, or a CSS string such as `'300px'` or `'100%'`. Under Next.js, use a number,
   * or `'100%'` for both `height` and `width` to fill the parent.
   */
  width: string | number;
  /**
   * How the image fits its `width` and `height`. On React Native, `center` shrinks an image that is
   * too big. Defaults to `cover`.
   */
  resizeMethod?: 'cover' | 'contain' | 'center';
  /** Rounds the corners with a radius token, such as `primary` (8px) or `round` for a circle. */
  borderRadius?: ThemeRadii;
  /**
   * Called once the image loads, with its `width`, `height` and `uri`. The size is the displayed
   * size on web and the file's size on React Native.
   */
  onLoad?: (event: onLoadProps) => void;
  /**
   * The image URL. `webProps.src` and `rxnProps.src` override it. On web, Image renders nothing
   * when there is no URL.
   */
  src?: string;
  /** Finds the image in tests. On web it becomes `data-testid`. */
  testID?: string;
  /**
   * Theme-aware styles for what props can't set. Applied after the margin and border props, so it
   * wins over them.
   */
  sx?:
    | SxStyleProp
    | Record<string, string | number | undefined | Array<string | number | undefined>>;
};

/** Props for web only. */
export type ImageWebOnlyProps = {
  /** The image URL, or `[mobile, tablet, desktop]` URLs for a srcset. Next.js ignores an array. */
  src?: string | SrcsetArray;
  /**
   * How wide the image shows, as a `sizes` string or one width per breakpoint. Defaults to `100vw`
   * when `src` is an array.
   */
  sizes?: string | string[];
  /**
   * The pixel width of each srcset file, as strings such as `'767'`. A `null` entry keeps its
   * default: `767`, `991` and `1440`. Ignored under Next.js.
   */
  srcsetWidths?: SrcsetArray;
  /** Loads the image straight away instead of when it nears the screen. Defaults to `false`. */
  eager?: boolean;
};

/** Props for React Native only. */
export type ImageRxnOnlyProps = {
  /** The image URL, or a bundled image from `require()`. */
  src?: string | number;
  /** Blurs the image by this radius, such as for a placeholder. */
  blurRadius?: number;
};

/** Image's props on every platform, with each platform's own props in `webProps` and `rxnProps`. */
export type ImageProps = ImageCommonProps & {
  /**
   * Props for web only. On web it also overrides `src`, `description`, `width` and `height`. Under
   * Next.js only `src`, `sizes` and `eager` take effect.
   */
  webProps?: Partial<Omit<ImageCommonProps, keyof ImageWebOnlyProps>> & ImageWebOnlyProps;
  /** Props for React Native only. It also overrides `src`, `width`, `height` and `resizeMethod`. */
  rxnProps?: Partial<Omit<ImageCommonProps, keyof ImageRxnOnlyProps | 'borderRadius'>> &
    ImageRxnOnlyProps;
};

export const NOT_FORWARDED_PROPS = [
  'borderRadius',
  'marginTop',
  'eager',
  'webSizes',
  'webSrcsetWidths',
  'webSrc',
  'rxnSrc',
  'rxnBlurRadius',
  'sx',
];

/** What `onLoad` receives. */
type onLoadProps = { width: number; height: number; uri: string };

export const BREAKPOINTS = breakpoints;

export const getSizes = (sizes: string | string[]): string => {
  if (Array.isArray(sizes)) {
    return sizes
      .map((size, index) =>
        index === sizes.length - 1 ? `${size}` : `(max-width: ${BREAKPOINTS[index]}) ${size}`
      )
      .join(', ');
  }
  return sizes;
};
